import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  MoneyInput,
  toast,
} from '@mlm/ui';
import { CREATOR_SUBSCRIPTION_PERIODS, CREATOR_SUBSCRIPTION_PERIOD_LABELS } from '@mlm/shared';
import { listSubscriptionPlans, saveSubscriptionPlans } from '../lib/api';

/**
 * LMS Phase 10 — the creator's own subscription pricing.
 *
 * Three periods, each independently switchable. The whole set is PUT at once
 * (never a delta), because "absent means unchanged" would make it impossible to
 * withdraw a period by unticking it.
 *
 * The nav only shows this page under the CREATOR subscription model, so the
 * page itself does not re-check: a creator who lands here by URL under the
 * tenant model sees an honest empty editor rather than a redirect loop.
 */

type Draft = { active: boolean; price: string };

const EMPTY: Draft = { active: false, price: '' };

export default function SubscriptionPlanPage() {
  const qc = useQueryClient();
  const { data: plans, isLoading } = useQuery({
    queryKey: ['creator-subscription-plans'],
    queryFn: listSubscriptionPlans,
  });
  const [draft, setDraft] = useState<Record<string, Draft>>({});

  // Seeded from the server once loaded, and re-seeded after a save so the
  // approval badges below always describe the prices actually on screen.
  useEffect(() => {
    if (!plans) return;
    const next: Record<string, Draft> = {};
    for (const period of CREATOR_SUBSCRIPTION_PERIODS) {
      const row = plans.find((p) => p.period === period);
      next[period] = row ? { active: row.active, price: row.price } : { ...EMPTY };
    }
    setDraft(next);
  }, [plans]);

  const save = useMutation({
    mutationFn: () =>
      saveSubscriptionPlans(
        CREATOR_SUBSCRIPTION_PERIODS.map((period) => ({
          period,
          active: draft[period]?.active === true,
          // A switched-off period still needs a valid price for the schema; it
          // is simply never offered to a buyer.
          price: draft[period]?.price?.trim() ? draft[period]!.price.trim() : '0',
        })),
      ),
    onSuccess: () => {
      toast.success('Subscription plans saved.');
      void qc.invalidateQueries({ queryKey: ['creator-subscription-plans'] });
    },
  });

  // A period that is switched ON with no real price would be sold for nothing.
  const invalid = CREATOR_SUBSCRIPTION_PERIODS.filter(
    (p) => draft[p]?.active && !(Number(draft[p]?.price) > 0),
  );

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Subscription plan</h1>
        <p className="text-sm text-muted-foreground">
          Members who subscribe get every one of your subscribers-only courses for as long as their
          subscription runs. Courses you price individually are not affected.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your periods</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {CREATOR_SUBSCRIPTION_PERIODS.map((period) => {
            const row = plans?.find((p) => p.period === period);
            const d = draft[period] ?? EMPTY;
            return (
              <div
                key={period}
                className="flex flex-wrap items-center gap-3 border-b pb-3 last:border-0"
              >
                <label className="flex min-w-40 items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={d.active}
                    onChange={(e) =>
                      setDraft({ ...draft, [period]: { ...d, active: e.target.checked } })
                    }
                  />
                  {CREATOR_SUBSCRIPTION_PERIOD_LABELS[period]}
                </label>
                <MoneyInput
                  className="h-9 w-40"
                  value={d.price}
                  placeholder="0.00"
                  onChange={(e) =>
                    setDraft({ ...draft, [period]: { ...d, price: e.target.value } })
                  }
                />
                {/* The creator's OWN view shows the approval state, because
                    they are the one who has to act on a rejection. */}
                {row?.approvalStatus === 'pending' && (
                  <Badge variant="warning">Awaiting review</Badge>
                )}
                {row?.approvalStatus === 'rejected' && (
                  <span className="flex items-center gap-2">
                    <Badge variant="muted">Not approved</Badge>
                    {row.rejectionReason && (
                      <span className="text-xs text-muted-foreground">{row.rejectionReason}</span>
                    )}
                  </span>
                )}
                {row?.approvalStatus === 'approved' && row.active && (
                  <Badge variant="outline">Live</Badge>
                )}
              </div>
            );
          })}

          {invalid.length > 0 && (
            <p className="text-xs text-destructive">
              Set a price above ₱0 for:{' '}
              {invalid.map((p) => CREATOR_SUBSCRIPTION_PERIOD_LABELS[p]).join(', ')}.
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button disabled={save.isPending || invalid.length > 0} onClick={() => save.mutate()}>
              Save
            </Button>
            <p className="text-xs text-muted-foreground">
              Changing a price sends that period back for admin approval. Members already subscribed
              keep the time they paid for.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
